import { fetchUserList } from "app/services/user/user.service";
import {
  getTransmissionProgressReportByUserId,
  getTransmissionReportLogsByUserId,
} from "app/services/userActivityLog/userActivityLog.service";
import { setUserList } from "app/store/user/slice/userSlice";
import { setUserTransmissionProgressData } from "app/store/userActivityLog/slice/userActivityLogSlice";
import { downloadFile } from "app/utils/file";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  USER_TRANSMISSION_PROGRESS_CHART_PALETTE,
  USER_TRANSMISSION_PROGRESS_TITLES,
} from "static/constants";
import { generateDateRangeArray, getStartOfPastMonth } from "utils/date";
import { showErrorToast, showSuccessToast } from "utils/toast";

const useTransmissionReportLog = () => {
  const { userList } = useSelector((state) => state.users);

  const { selectedUser, userTransmissionProgressData } = useSelector(
    (state) => state.userActivityLog
  );

  const [isDownloading, setIsDownloading] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [modifiedUserList, setModifiedUserList] = useState([]);
  const [isFetchingProgressReport, setIsFetchingProgressReport] =
    useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    if (userList.length) {
      const modifiedUserList = userList.map((user) => ({
        ...user,
        label: `${user.username} (${user.label})`,
      }));

      setModifiedUserList(modifiedUserList);
    }

    return () => {
      setModifiedUserList([]);
    };
  }, [userList]);

  const onClose = () => {
    setShowUserModal(false);
  };

  const fetchUsers = async () => {
    if (userList.length > 0) {
      return;
    }
    const users = await fetchUserList();
    const filteredUser = users
      ?.filter((user) => user.active)
      .map((user) => {
        return { id: user._id, label: user.email, username: user.name };
      });

    dispatch(setUserList(filteredUser));
  };

  const getTransmissionReportLogs = async () => {
    try {
      setIsDownloading(true);
      if (!selectedUser) {
        return;
      }

      const response = await getTransmissionReportLogsByUserId(selectedUser.id);
      if (response.error) {
        throw response.error;
      }

      if (response.data && response.filename) {
        const { filename } = response;
        downloadFile(response, filename);
        showSuccessToast("User activity log downloaded successfully.");
        onClose();
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      showErrorToast(error.message);
    } finally {
      setIsDownloading(false);
    }
  };

  const getUserProgressReport = async () => {
    try {
      setIsFetchingProgressReport(true);
      if (!selectedUser) {
        return;
      }

      const response = await getTransmissionProgressReportByUserId(
        selectedUser.id
      );
      if (response.error) {
        throw response.error;
      }

      if (response.data) {
        generateChartData(response.data);
        showSuccessToast(response.message);
        onClose();
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      showErrorToast(error.message);
    } finally {
      setIsFetchingProgressReport(false);
    }
  };

  const generateChartData = (userTransmissionProgressData) => {
    if (!userTransmissionProgressData) {
      return;
    }

    const { dateArray, dailyReportCountObj } = prepareChartData(
      userTransmissionProgressData
    );

    const palette = USER_TRANSMISSION_PROGRESS_CHART_PALETTE;
    const datasets = Object.keys(dailyReportCountObj).map((key) => ({
      label: key,
      data: dailyReportCountObj[key],
      borderColor: palette[key],
      backgroundColor: palette[key],
    }));

    const maxCount = Math.max(
      ...datasets.flatMap((dataset) =>
        dataset.data.filter((value) => value !== null)
      )
    );

    const chartData = {
      labels: dateArray,
      datasets,
    };

    dispatch(
      setUserTransmissionProgressData({
        chartData,
        maxCount,
      })
    );
  };

  const prepareChartData = (userTransmissionProgressData) => {
    const dateArray = generateDateRangeArray(getStartOfPastMonth(1).toDate());
    const dailyReportCountObj = {};
    Object.keys(userTransmissionProgressData).forEach((key) => {
      dailyReportCountObj[USER_TRANSMISSION_PROGRESS_TITLES[key]] =
        getReportCountArrayBasedOnDate(
          dateArray,
          userTransmissionProgressData[key]
        );
    });

    return { dateArray, dailyReportCountObj };
  };

  const getReportCountArrayBasedOnDate = (dates, reportCounts) => {
    const reportCountArrayBasedOnDate = Array(dates.length).fill(null);
    reportCounts.forEach((report) => {
      const indexOfReportCount = dates.indexOf(report.date);
      if (indexOfReportCount !== -1)
        reportCountArrayBasedOnDate[indexOfReportCount] = report.count;
    });

    return reportCountArrayBasedOnDate;
  };

  const getChartOptions = () => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      aspectRatio: 1,
      interaction: {
        mode: "index",
        intersect: false,
      },
      plugins: {
        tooltip: {
          enabled: true,
          position: "nearest",
          padding: 10,
        },
        legend: {
          position: "bottom",
          labels: {
            boxWidth: 8,
            boxHeight: 5,
            usePointStyle: true,
          },
        },
      },

      scales: {
        y: {
          beginAtZero: true,
          min: 0,
          max:
            userTransmissionProgressData?.maxCount &&
            userTransmissionProgressData?.maxCount > 20
              ? undefined
              : 20,
          ticks: {
            stepSize: 5,
          },
          title: {
            display: true,
            text: "No. of Transmission(s)",
          },
        },
        x: {
          ticks: {
            color: "#333333",
            fontSize: 12,
            autoSkip: true,
            maxTicksLimit: 10,
            maxRotation: 0,
            minRotation: 0,
          },
        },
      },
      elements: {
        line: {
          fill: false,
          spanGaps: true,
        },
      },
    };
  };

  return {
    userList,
    modifiedUserList,
    selectedUser,
    showUserModal,
    isDownloading,
    isFetchingProgressReport,
    userTransmissionProgressData,

    onClose,
    dispatch,
    fetchUsers,
    getChartOptions,
    setShowUserModal,
    getUserProgressReport,
    getTransmissionReportLogs,
  };
};

export default useTransmissionReportLog;
